import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import SliderAtom from '../../components/atoms/Slider/SliderAtom';
import ButtonAtom from '../../components/atoms/Button/ButtonAtom';
import CheckboxAtom from '../../components/atoms/checkbox/Checkbox';
import SwitchAtom from '../../components/atoms/Switch/SwitchAtom';
import InputAtom from '../../components/atoms/Input/InputAtom';
import TextareaAtom from '../../components/atoms/Textarea/TextareaAtom';
import DropdownAtom from '../../components/atoms/Dropdown/DropdownAtom';
import InputNumberAtom, { InputNumberAtomType } from '../../components/atoms/InputNumber/InputNumberAtom';
import DatePickerAtom from '../../components/atoms/DatePicker/DatePickerAtom';
import DateTimePickerInputAtom from '../../components/atoms/DateTimePickerInput/DateTimePickerInputAtom';
import UnitValueInputAtom from '../../components/atoms/UnitValueInput/UnitValueInputAtom';
import SphereReferenceInputMolecule from '../../components/molecules/SphereReference/SphereReferenceInputMolecule';
import ChipsAtom from '../../components/atoms/Chips/ChipsAtom';
import type { SchemaProperty, UnitConfig, UnitValue } from 'types/schema';
import type { Adapter } from '../types';
import './interactiveAdapters.scss';

const VARIANT_MAP: Record<string, { variant?: string; severity?: string }> = {
  primary: {},
  secondary: { variant: 'outlined' },
  text: { variant: 'text' },
  danger: { severity: 'danger' },
};

/**
 * `ctx.onAction` fires the same way in `live` and `preview` mode (SC10, SC11) — the composer's Try
 * mode routes preview actions into its action log / wire delivery, so the adapter must never
 * swallow them. No adapter here calls a mutation client directly; the only thing "preview never
 * writes" constrains is whatever the host wires `onAction` to, which is out of this file's control.
 */
export const ButtonAdapter: Adapter = ({ componentId, props, ctx }) => {
  const label = ctx.resolveValue(props.label) as string;
  const mapping = VARIANT_MAP[(props.variant as string) ?? 'primary'] ?? {};
  const onActionName = props.onAction as string | undefined;

  // Action-context minimization (ADR-043 §7 / SC11): the dispatched action carries ONLY the
  // payload the document explicitly bound via `actionContext`. Spreading `ctx.dataModel` here
  // would hand every wire target the whole surface model on every click — the implicit
  // full-model dump the format exists to forbid.
  const boundContext = props.actionContext !== undefined ? ctx.resolveValue(props.actionContext) : undefined;
  const actionContext =
    boundContext && typeof boundContext === 'object' && !Array.isArray(boundContext)
      ? (boundContext as Record<string, unknown>)
      : undefined;

  const handleClick = () => {
    if (!onActionName) return;
    ctx.onAction({ name: onActionName, componentId, context: actionContext });
  };

  return (
    <div data-component-id={componentId}>
      <ButtonAtom
        label={label}
        icon={props.icon ? `pi pi-${props.icon}` : undefined}
        disabled={props.disabled as boolean}
        onClick={handleClick}
        variant={mapping.variant as never}
        severity={mapping.severity as never}
      />
    </div>
  );
};


/**
 * Value equality for adapter props. `Object.is` alone is wrong for the object/array-shaped values
 * these adapters carry (`UnitValue`, `string[]`, `Date`) — a parent that rebuilds the object each
 * render would otherwise look like a fresh incoming value on every keystroke and clobber the edit
 * in progress.
 */
const sameAdapterValue = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
  return JSON.stringify(a) === JSON.stringify(b);
};

/**
 * Local edit state that stays in sync with the value flowing in from the host.
 *
 * Seeding `useState` from a prop captures it ONCE. These adapters are the type-default `edit`
 * renderers for every property in `AdditionalPropertiesGrid`, `SphereFieldInputMolecule` and
 * `SphereEditor`'s docked tabs, and those hosts legitimately replace the value under a mounted
 * adapter — Formik `enableReinitialize` on sphere switch or post-save, schema `default_value`s
 * landing after first paint, `resetForm` on cancel. Without a resync the control keeps showing the
 * old value while the form holds the new one, and the next keystroke writes the stale one back.
 *
 * Only a genuinely CHANGED incoming value resyncs, so echoing the value the user just typed back
 * through the host does not fight the cursor.
 */
function useHostSyncedValue<T>(incoming: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [local, setLocal] = useState<T>(incoming);
  const lastIncoming = useRef<T>(incoming);

  useEffect(() => {
    if (sameAdapterValue(lastIncoming.current, incoming)) return;
    lastIncoming.current = incoming;
    setLocal(incoming);
  }, [incoming]);

  return [local, setLocal];
}

export const TextFieldAdapter: Adapter = ({ componentId, props, ctx }) => {
  const label = ctx.resolveValue(props.label) as string;
  // A host that renders the field without a visible label (e.g. UnifiedPropertyRendererMolecule,
  // where the surrounding form owns the label) supplies the field's name here so the control is
  // never unnamed.
  const ariaLabel = !label && typeof props.ariaLabel === 'string' ? props.ariaLabel : undefined;
  const resolvedValue = props.value !== undefined ? ctx.resolveValue(props.value) : undefined;
  const placeholder = props.placeholder !== undefined ? (ctx.resolveValue(props.placeholder) as string) : undefined;
  const variant = (props.variant as string) ?? 'shortText';
  const [localValue, setLocalValue] = useHostSyncedValue<string>((resolvedValue as string) ?? '');

  const handleChange = (value: string) => {
    setLocalValue(value);
    const onValueChanged = props.onValueChanged as string | undefined;
    if (onValueChanged) {
      ctx.onAction({ name: onValueChanged, componentId, context: { value } });
    }
  };

  if (variant === 'longText') {
    return (
      <div data-component-id={componentId}>
        <TextareaAtom
          label={label}
          value={localValue}
          placeholder={placeholder}
          disabled={props.disabled as boolean}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleChange(e.target.value)}
          rows={4}
          aria-label={ariaLabel}
        />
      </div>
    );
  }

  const inputType = variant === 'number' ? 'number' : variant === 'email' ? 'email' : 'text';

  return (
    <div data-component-id={componentId}>
      {label && <label className="interactive-adapter__label" htmlFor={componentId}>{label}</label>}
      <InputAtom
        id={componentId}
        value={localValue}
        placeholder={placeholder}
        disabled={props.disabled as boolean}
        type={inputType}
        aria-label={ariaLabel}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e.target.value)}
      />
    </div>
  );
};

export const CheckboxAdapter: Adapter = ({ componentId, props, ctx }) => {
  const label = ctx.resolveValue(props.label) as string;
  const resolvedChecked = props.checked !== undefined ? ctx.resolveValue(props.checked) : false;
  const [localChecked, setLocalChecked] = useState<boolean>(Boolean(resolvedChecked));

  const handleChange = (checked: boolean) => {
    setLocalChecked(checked);
    const onToggled = props.onToggled as string | undefined;
    if (onToggled) {
      ctx.onAction({ name: onToggled, componentId, context: { checked } });
    }
  };

  return (
    <div data-component-id={componentId}>
      <CheckboxAtom inputId={componentId} label={label} initialChecked={localChecked} onChange={handleChange} />
    </div>
  );
};

export const DropdownAdapter: Adapter = ({ componentId, props, ctx }) => {
  const label = ctx.resolveValue(props.label) as string;
  const resolvedValue = props.value !== undefined ? ctx.resolveValue(props.value) : undefined;
  const options = (ctx.resolveValue(props.options) as Array<Record<string, unknown>>) ?? [];
  const placeholder = props.placeholder !== undefined ? (ctx.resolveValue(props.placeholder) as string) : undefined;
  const [localValue, setLocalValue] = useHostSyncedValue<unknown>(resolvedValue);

  const handleChange = (e: { value: unknown }) => {
    setLocalValue(e.value);
    const onValueChanged = props.onValueChanged as string | undefined;
    if (onValueChanged) {
      ctx.onAction({ name: onValueChanged, componentId, context: { value: e.value } });
    }
  };

  return (
    <div data-component-id={componentId}>
      {label && <label className="interactive-adapter__label" htmlFor={componentId}>{label}</label>}
      <DropdownAtom
        inputId={componentId}
        ariaLabel={label || (typeof props.ariaLabel === 'string' ? props.ariaLabel : undefined)}
        value={localValue}
        options={options}
        optionLabel={(props.optionLabel as string) ?? 'label'}
        optionValue={(props.optionValue as string) ?? 'value'}
        placeholder={placeholder}
        disabled={props.disabled as boolean}
        onChange={handleChange}
      />
    </div>
  );
};

export const NumberSliderAdapter: Adapter = ({ componentId, props, ctx }) => {
  const { t } = useTranslation();
  const label = props.label !== undefined ? (ctx.resolveValue(props.label) as string) : undefined;
  const resolvedValue = props.value !== undefined ? ctx.resolveValue(props.value) : undefined;
  const min = (props.min as number) ?? 0;
  const max = (props.max as number) ?? 100;
  const step = (props.step as number) ?? 1;
  const showValue = props.showValue !== false;
  const [localValue, setLocalValue] = useState<number>(Number(resolvedValue) || min);

  const handleChange = (value: number) => {
    setLocalValue(value);
    const onValueChanged = props.onValueChanged as string | undefined;
    if (onValueChanged) {
      ctx.onAction({ name: onValueChanged, componentId, context: { value } });
    }
  };

  return (
    <div data-component-id={componentId} className="interactive-adapter__slider">
      {(label || showValue) && (
        <div className="interactive-adapter__slider-header">
          {label && <span>{label}</span>}
          {showValue && <span>{localValue}</span>}
        </div>
      )}
      <SliderAtom
        value={localValue}
        min={min}
        max={max}
        step={step}
        disabled={props.disabled as boolean}
        onChange={handleChange}
        ariaLabel={label || t('space.render.numberSliderLabel')}
      />
    </div>
  );
};

// ─── Renderer primitives (EAINT-855) ─────────────────────────────────────────
// Internal edit-mode adapters `TYPE_DEFAULT_RENDERERS` names — thin wrappers over existing
// design-system atoms, following the same (componentId, props, ctx) → local-state → onAction
// pattern as every adapter above.

export const NumberFieldAdapter: Adapter = ({ componentId, props, ctx }) => {
  const { t } = useTranslation();
  const label = props.label !== undefined ? (ctx.resolveValue(props.label) as string) : undefined;
  const resolvedValue = props.value !== undefined ? ctx.resolveValue(props.value) : null;
  const [localValue, setLocalValue] = useHostSyncedValue<number | null>(
    typeof resolvedValue === 'number' ? resolvedValue : null
  );

  const handleChange = (value: number | null) => {
    setLocalValue(value);
    const onValueChanged = props.onValueChanged as string | undefined;
    if (onValueChanged) {
      ctx.onAction({ name: onValueChanged, componentId, context: { value } });
    }
  };

  return (
    <div data-component-id={componentId}>
      {label && <label className="interactive-adapter__label" htmlFor={componentId}>{label}</label>}
      <InputNumberAtom
        inputId={componentId}
        ariaLabel={label || t('space.render.numberFieldLabel')}
        value={localValue}
        onChange={handleChange}
        type={props.numberType as InputNumberAtomType}
        currency={props.currency as string}
        min={props.min as number}
        max={props.max as number}
        step={props.step as number}
        precision={props.precision as number}
        placeholder={props.placeholder !== undefined ? (ctx.resolveValue(props.placeholder) as string) : undefined}
        disabled={props.disabled as boolean}
        showButtons={props.showButtons as boolean}
        invalid={props.invalid as boolean}
      />
    </div>
  );
};

export const DateFieldAdapter: Adapter = ({ componentId, props, ctx }) => {
  const { t } = useTranslation();
  const label = props.label !== undefined ? (ctx.resolveValue(props.label) as string) : undefined;
  const resolvedValue = props.value !== undefined ? ctx.resolveValue(props.value) : null;
  const [localValue, setLocalValue] = useHostSyncedValue<string | Date | null>(resolvedValue as string | Date | null);

  const handleDateChange = (date: Date | null) => {
    setLocalValue(date);
    const onValueChanged = props.onValueChanged as string | undefined;
    if (onValueChanged) {
      ctx.onAction({ name: onValueChanged, componentId, context: { value: date ? date.toISOString() : null } });
    }
  };

  return (
    <div data-component-id={componentId}>
      <DatePickerAtom
        name={componentId}
        value={localValue}
        onDateChange={handleDateChange}
        dateFormat={props.dateFormat as string}
        selectionMode={props.selectionMode as 'single' | 'range' | 'multiple' | undefined}
        label={label}
        // `DatePickerAtom` only renders a visible `<label>` (and its `htmlFor`) when `label`
        // is truthy — `label` is optional on this catalog entry, so the underlying input still
        // needs a name when the author omits one. Flows through to `CalendarAtom`/`PrimeCalendar`
        // via their `...rest` passthrough (neither declares `ariaLabel` explicitly).
        ariaLabel={label || t('space.render.dateFieldLabel')}
        disabled={props.disabled as boolean}
      />
    </div>
  );
};

export const DateTimeFieldAdapter: Adapter = ({ componentId, props, ctx }) => {
  const { t } = useTranslation();
  const label = props.label !== undefined ? (ctx.resolveValue(props.label) as string) : undefined;
  const resolvedValue = props.value !== undefined ? ctx.resolveValue(props.value) : null;
  const [localValue, setLocalValue] = useHostSyncedValue<string | null>(resolvedValue as string | null);

  const handleIsoChange = (iso: string | null) => {
    setLocalValue(iso);
    const onValueChanged = props.onValueChanged as string | undefined;
    if (onValueChanged) {
      ctx.onAction({ name: onValueChanged, componentId, context: { value: iso } });
    }
  };

  return (
    <div data-component-id={componentId}>
      <DateTimePickerInputAtom
        value={localValue}
        onIsoChange={handleIsoChange}
        label={label}
        ariaLabel={label || t('space.render.dateFieldLabel')}
        disabled={props.disabled as boolean}
      />
    </div>
  );
};

export const UnitFieldAdapter: Adapter = ({ componentId, props, ctx }) => {
  const { t } = useTranslation();
  const resolvedValue = props.value !== undefined ? ctx.resolveValue(props.value) : null;
  const unitConfig = props.unitConfig as UnitConfig;
  const [localValue, setLocalValue] = useHostSyncedValue<number | Partial<UnitValue> | UnitValue | null>(
    resolvedValue as number | Partial<UnitValue> | UnitValue | null
  );

  const handleChange = (value: UnitValue | null) => {
    setLocalValue(value);
    const onValueChanged = props.onValueChanged as string | undefined;
    if (onValueChanged) {
      ctx.onAction({ name: onValueChanged, componentId, context: { value } });
    }
  };

  return (
    <div data-component-id={componentId}>
      {/* No `label` prop exists on this catalog entry (§ `UnitField` props) — the author has no
          way to supply an accessible name, so this adapter must always fall back to one. */}
      <UnitValueInputAtom
        value={localValue}
        onChange={handleChange}
        unitConfig={unitConfig}
        min={props.min as number}
        max={props.max as number}
        step={props.step as number}
        integer={props.integer as boolean}
        precision={props.precision as number}
        showStepper={props.showStepper as boolean}
        disabled={props.disabled as boolean}
        invalid={props.invalid as boolean}
        ariaLabel={t('space.render.unitFieldLabel')}
      />
    </div>
  );
};

export const ReferenceFieldAdapter: Adapter = ({ componentId, props, ctx }) => {
  const { t } = useTranslation();
  const resolvedValue = props.value !== undefined ? ctx.resolveValue(props.value) : null;
  const [localValue, setLocalValue] = useHostSyncedValue<string | null>(resolvedValue as string | null);

  // SphereReferenceInputMolecule reads targetSchemaSystemName/displayProperty off
  // `property.metadata` (schema-authoring shape) — this adapter has no real SchemaProperty,
  // so it synthesizes the minimal shape the molecule actually reads.
  const property = {
    name: componentId,
    type: 'reference',
    metadata: {
      targetSchemaSystemName: props.targetSchemaSystemName as string,
      displayProperty: props.displayProperty as string | undefined,
    },
  } as unknown as SchemaProperty;

  const handleChange = (id: string | null) => {
    setLocalValue(id);
    const onValueChanged = props.onValueChanged as string | undefined;
    if (onValueChanged) {
      ctx.onAction({ name: onValueChanged, componentId, context: { value: id } });
    }
  };

  return (
    <div data-component-id={componentId}>
      {/* No `label` prop exists on this catalog entry either — same fallback as `UnitField`. */}
      <SphereReferenceInputMolecule
        property={property}
        value={localValue}
        onChange={handleChange}
        disabled={props.disabled as boolean}
        ariaLabel={t('space.render.referenceFieldLabel')}
      />
    </div>
  );
};

export const TagListFieldAdapter: Adapter = ({ componentId, props, ctx }) => {
  const { t } = useTranslation();
  const resolvedValue = props.value !== undefined ? ctx.resolveValue(props.value) : [];
  const [localValue, setLocalValue] = useHostSyncedValue<string[]>(Array.isArray(resolvedValue) ? resolvedValue : []);

  const handleChange = (value: string[]) => {
    setLocalValue(value);
    const onValueChanged = props.onValueChanged as string | undefined;
    if (onValueChanged) {
      ctx.onAction({ name: onValueChanged, componentId, context: { value } });
    }
  };

  return (
    <div data-component-id={componentId}>
      {/* No `label` prop exists on this catalog entry — same fallback as `UnitField`. */}
      <ChipsAtom
        value={localValue}
        onChange={handleChange}
        placeholder={props.placeholder !== undefined ? (ctx.resolveValue(props.placeholder) as string) : undefined}
        disabled={props.disabled as boolean}
        aria-label={t('space.render.tagListFieldLabel')}
      />
    </div>
  );
};

/** The structural value a JSON textarea's current text denotes, or `NOT_JSON` while mid-edit. */
const NOT_JSON = Symbol('notJson');
const jsonTextMeans = (text: string): unknown | typeof NOT_JSON => {
  try {
    // Empty text denotes `null`, matching what this adapter has always emitted for a cleared field.
    return text.trim() === '' ? null : JSON.parse(text);
  } catch {
    return NOT_JSON;
  }
};

export const JsonFieldAdapter: Adapter = ({ componentId, props, ctx }) => {
  const { t } = useTranslation();
  const resolvedValue = props.value !== undefined ? ctx.resolveValue(props.value) : undefined;
  // Deliberately NOT `useHostSyncedValue`: that hook compares the incoming value to the previous
  // incoming value and resyncs local state whenever they differ. This adapter's local state is
  // FORMATTED TEXT while its emitted value is the PARSED object, so the host echoing back the
  // value the user just typed always looks different from the typed text — the textarea would be
  // rewritten to pretty-printed form and the caret would jump to the end on every keystroke.
  // Here the guard compares what the text MEANS to the incoming value instead.
  const [localText, setLocalText] = useState<string>(resolvedValue !== undefined ? JSON.stringify(resolvedValue, null, 2) : '');
  const [invalid, setInvalid] = useState(false);
  const lastIncoming = useRef<unknown>(resolvedValue);

  useEffect(() => {
    if (sameAdapterValue(lastIncoming.current, resolvedValue)) return;
    lastIncoming.current = resolvedValue;
    const means = jsonTextMeans(localText);
    // The text already denotes the incoming value (the user's own edit echoing back) — leave it,
    // and with it the caret. A genuinely external change (Formik `enableReinitialize`, `resetForm`,
    // a late `default_value`) does not match, and rewrites.
    // `?? null` so a cleared textarea and an absent incoming value compare equal.
    if (means !== NOT_JSON && sameAdapterValue(means, resolvedValue ?? null)) return;
    setLocalText(resolvedValue !== undefined ? JSON.stringify(resolvedValue, null, 2) : '');
    setInvalid(false);
  }, [resolvedValue, localText]);

  const handleChange = (text: string) => {
    setLocalText(text);
    const onValueChanged = props.onValueChanged as string | undefined;
    const parsed = jsonTextMeans(text);
    if (parsed === NOT_JSON) {
      // Invalid JSON mid-edit is expected while typing — hold the action until it parses.
      setInvalid(true);
      return;
    }
    setInvalid(false);
    // Keep the resync guard's baseline in step with what we emit, so the host's echo of this exact
    // value is recognized as our own and never rewrites the textarea.
    lastIncoming.current = parsed;
    if (onValueChanged) {
      ctx.onAction({ name: onValueChanged, componentId, context: { value: parsed } });
    }
  };

  return (
    <div data-component-id={componentId} className={invalid ? 'interactive-adapter__json-field--invalid' : undefined}>
      {/* No `label` prop exists on this catalog entry — same fallback as `UnitField`. */}
      <TextareaAtom
        value={localText}
        disabled={props.disabled as boolean}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleChange(e.target.value)}
        rows={6}
        aria-label={t('space.render.jsonFieldLabel')}
      />
    </div>
  );
};

/**
 * `BooleanField` — the boolean EDIT type default (EAINT-855).
 *
 * Speaks the property-render contract (`value` + `onValueChanged`) that `CatalogAdapterHost`
 * supplies, unlike the older `CheckBox` primitive (`checked` + `onToggled`), and wraps
 * `SwitchAtom` to preserve the exact affordance the pre-unification grid and field-input
 * rendered for boolean properties.
 *
 * Fully controlled: the value prop is the single source of truth, so programmatic writes
 * (Formik `setFieldValue`, `resetForm`) stay reflected in the UI.
 */
export const BooleanFieldAdapter: Adapter = ({ componentId, props, ctx }) => {
  const { t } = useTranslation();
  const label = props.label !== undefined ? (ctx.resolveValue(props.label) as string) : undefined;
  const resolvedValue = props.value !== undefined ? ctx.resolveValue(props.value) : false;

  const handleChange = (checked: boolean) => {
    const onValueChanged = props.onValueChanged as string | undefined;
    if (onValueChanged) {
      ctx.onAction({ name: onValueChanged, componentId, context: { value: checked } });
    }
  };

  return (
    <div data-component-id={componentId}>
      <SwitchAtom
        inputId={componentId}
        label={label ?? ''}
        ariaLabel={label || t('space.render.booleanFieldLabel')}
        checked={Boolean(resolvedValue)}
        onChange={handleChange}
        disabled={props.disabled as boolean}
      />
    </div>
  );
};
